<template>
  <dsf-dropdown-wrapper
    ref="dropdownWrapper"
    class="el-select dsf-area-cascader"
    :disabled="disabled"
    max-height="295px"
    height="295px"
    lazy
    @visible-change="visibleChange">
    <template #default>
      <el-input-pro
        ref="reference"
        :value="selectedText"
        :placeholder="placeholder"
        :name="name"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :validate-event="false"
        :class="{ 'is-focus': visible }"
        readonly
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.native.esc.stop.prevent="visible = false"
        @keydown.native.tab="visible = false"
        @mouseenter.native="inputHovering = true"
        @mouseleave.native="inputHovering = false">
        <template slot="prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </template>
        <template slot="suffix">
          <i v-if="inputHovering && showCloseCopy" class="el-select__caret el-input__icon el-icon-circle-close" @click.stop="handleClearClick"></i>
          <i v-show="!inputHovering || !showCloseCopy" :class="['el-select__caret', 'el-input__icon', 'el-icon-' + iconClass]"></i>
        </template>
      </el-input-pro>
    </template>
    <template #dropdown>
      <dsf-area-cascader-panel
        ref="panel"
        :super-code="realSuperCode"
        :accuracy="accuracy"
        :check-strictly="checkStrictly"
        :options-text-field="optionsTextField"
        :selected-text-field="selectedTextField"
        @change="handleChange"
      />
    </template>
  </dsf-dropdown-wrapper>
</template>

<script>
import { getValuesByLastValue } from '_platform/output/area/areaUtils';

export default {
  name: "DsfAreaCascader",
  props: {
    name: String,
    autocomplete: {
      type: String,
      default: 'off'
    },
    value: {
      type: Object,
      default() {
        return {};
      }
    },
    placeholder: {
      type: String,
      default: "请选择"
    },
    superCode: {
      type: String,
      default: "000000000000"
    },
    // 精确度，选择到第几级: 0-省、1-市、2-区县、3-街道、4-村居
    accuracy: {
      type: Number,
      default: 4,
    },
    // 输入框中是否显示选中值的完整路径
    showAllLevels: {
      type: Boolean,
      default: true
    },
    // 是否可选择任意一级
    checkStrictly: {
      type: Boolean,
      default: false
    },
    // 提交数据是否包含别名
    postHasAlias: {
      type: Boolean,
      default: false
    },
    // 选项中显示的文本字段
    optionsTextField: {
      type: String,
      default: "name"
    },
    //选中时显示的值
    selectedTextField: {
      type: String,
      default: "name"
    },
    disabled: Boolean,
    readonly: Boolean,
    showClose: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      visible: false,
      softFocus: false,
      inputHovering: false,
      valueCopy: {}
    }
  },
  computed: {
    realSuperCode() {
      let code = this.superCode.trim();
      if (!/^(\d+\.)*\d+$/.test(code) || code == '000000000000') {
        return "000000000000";
      }
      code = code.split('.');
      if (code.length == 1) {
        code = getValuesByLastValue(code[0]);
      }
      return code.join('.');
    },
    rangeStart() {
      let codes = this.realSuperCode.split('.');
      return codes.length - 1;
    },
    rangeEnd() {
      return this.accuracy;
    },
    iconClass() {
      return this.visible ? 'arrow-up is-reverse' : 'arrow-up';
    },
    // 对value值得value逆向解析出所有的key
    // 用于数据复现
    ids() {
      if (!this.valueCopy?.value) {
        return [];
      }
      return getValuesByLastValue(this.valueCopy.value);
    },
    selectedText() {
      let val = this.valueCopy;
      if (this.selectedTextField === 'alias') {
        return val?.alias || val?.text || '';
      }
      return val?.text || '';
    },
    showCloseCopy() {
      if (!this.showClose) return false;
      return !!this.selectedText;
    },
  },
  watch: {
    'value.value': {
      handler(to) {
        this.valueCopy = this.value;
      },
      immediate: true
    },
    rangeStart: {
      handler(to) {
        if (to > this.rangeEnd) {
          this.$emit('update:accuracy', to);
        }
      },
      immediate: true
    },
    rangeEnd: {
      handler(to) {
        if (to < this.rangeStart) {
          this.$emit('update:accuracy', this.rangeStart);
        }
      },
      immediate: true
    }
  },
  methods: {
    getValuesByLastValue,
    getDefaultValue() {
      let res = { text: "", value: "" };
      if (this.postHasAlias) {
        res.alias = "";
      }
      return res;
    },
    visibleChange(visible) {
      if (visible) {
        let {rangeStart: s, rangeEnd: e} = this;
        let val = _.slice(this.ids, +s + 1, +e + 2);
        this.$refs.panel.setValue(val);
        this.$refs.panel.scrollIntoView();
      }
      this.visible = visible;
    },
    handleFocus(event) {
      if (!this.softFocus) {
        if (this.automaticDropdown || this.filterable) {
          this.visible = true;
        }
        this.$emit('focus', event);
      } else {
        this.softFocus = false;
      }
    },
    handleBlur(event) {
      setTimeout(() => {
        this.$emit('blur', event);
      }, 50);
      this.softFocus = false;
    },
    handleClearClick() {
      let res = this.getDefaultValue();
      this.valueCopy = res;
      this.$emit('input', res);
      this.$nextTick(() => {
        this.$emit('change', res);
      });
    },
    handleChange({ nodes, values }) {
      let res = this.getDefaultValue();
      if (values.length > 0) {
        let text = [], alias = [];
        _.each(nodes, ({data}) => {
          text.push(data.name);
          alias.push(data.alias);
        });
        res = {
          text: this.showAllLevels ? text.join("-") : _.last(text),
          value: _.last(values)
        };
        if (this.postHasAlias) {
          res["alias"] = this.showAllLevels ? alias.join("-") : _.last(alias);
        }
      }
      if (res.value !== this.valueCopy.value) {
        this.$refs.dropdownWrapper.toggleDropDownVisible(false);
        this.valueCopy = res;
        this.$emit('input', res);
        this.$nextTick(() => {
          this.$emit('change', res);
        });
      }
    }
  }
}
</script>